import React, { useState, useCallback } from "react";
import type { BaseEntity, FieldValue } from "../types";

interface UseMobileDetailModalEditingReturn<T extends BaseEntity> {
  editingField: string | null;
  isEditing: boolean;
  editedItem: T;
  handleFieldEdit: (fieldId: string) => void;
  handleSaveField: (fieldId: string, onInlineEdit?: (item: T) => Promise<void>) => Promise<void>;
  handleCancelEdit: () => void;
  handleFieldValueChange: (fieldId: string, newValue: FieldValue) => void;
}

/**
 * Custom hook for managing mobile detail modal editing state
 * Handles field editing, value changes, and save/cancel operations
 */
export function useMobileDetailModalEditing<T extends BaseEntity>(
  item: T
): UseMobileDetailModalEditingReturn<T> {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState<T>(item);

  // Update edited item when item prop changes
  React.useEffect(() => {
    setEditedItem(item);
  }, [item]);

  // Handle field edit - MEMOIZED
  const handleFieldEdit = useCallback((fieldId: string) => {
    setEditingField(fieldId);
    setIsEditing(true);
  }, []);

  // Handle save field - MEMOIZED
  const handleSaveField = useCallback(
    async (fieldId: string, onInlineEdit?: (item: T) => Promise<void>) => {
      try {
        if (onInlineEdit) {
          await onInlineEdit(editedItem);
        }
        setEditingField(null);
        setIsEditing(false);
      } catch (error) {
        console.error("Failed to save field:", error);
      }
    },
    [editedItem]
  );

  // Handle cancel edit - MEMOIZED
  const handleCancelEdit = useCallback(() => {
    setEditedItem(item); // Reset to original
    setEditingField(null);
    setIsEditing(false);
  }, [item]);

  // Handle field value change - MEMOIZED
  const handleFieldValueChange = useCallback((fieldId: string, newValue: FieldValue) => {
    setEditedItem((prev) => ({
      ...prev,
      [fieldId]: newValue,
    }));
  }, []);

  return {
    editingField,
    isEditing,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  };
}
